import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { DbEnums } from '@eluve/graphql-types';

const c = initContract();

export const adminAppointmentContract = c.router(
  {
    deleteAppointmentAssets: {
      method: 'POST',
      path: '/asset-deletion/:tenantId/:appointmentId',
      body: z.object({
        jobType: z.enum([
          DbEnums.AssetDeletionJobType.AUDIO_FOR_APPOINTMENT,
          DbEnums.AssetDeletionJobType.TRANSCRIPTS_AND_AUDIO_FOR_APPOINTMENT,
          DbEnums.AssetDeletionJobType.APPOINTMENT_OUTPUTS,
        ]),
      }),
      responses: {
        201: z.object({
          jobId: z.string(),
          willRunAt: z.date().optional(),
        }),
        400: z.object({
          message: z.string(),
        }),
      },
    },
    getAppointmentFileSignedUrl: {
      method: 'GET',
      path: '/file/:tenantId/:appointmentId/:fileId',
      responses: {
        200: z.object({
          presignedUrl: z.string().nullable(),
        }),
      },
      summary:
        'Requests a presigned GCS Url for downloading an appointment file',
    },
    overrideAudioFileAndTranscriptAsCancelled: {
      method: 'PATCH',
      path: '/file/:tenantId/:appointmentId/:fileId',
      responses: {
        200: z.object({
          transcriptUpdated: z.boolean(),
          audioFileUpdated: z.boolean(),
        }),
      },
      body: z.object({}),
    },
    rescheduleTranscription: {
      method: 'POST',
      path: '/file/:tenantId/:appointmentId/:fileId/reschedule-transcription',
      responses: {
        200: z.object({
          transcriptId: z.string(),
          scheduled: z.boolean(),
        }),
        404: z.object({
          message: z.string(),
        }),
        422: z.object({
          message: z.string(),
          currentStatus: z.string(),
        }),
      },
      body: z.object({}),
      summary: 'Reschedule a failed transcription for an audio file',
    },
    regenerateSummary: {
      method: 'POST',
      path: '/regenerate-summary/:tenantId/:appointmentId',
      responses: {
        201: z.discriminatedUnion('mode', [
          z.object({
            mode: z.literal('summary_started'),
            jobId: z.string(),
          }),
          z.object({
            mode: z.literal('transcription_started'),
            scheduledTranscriptionCount: z.number(),
          }),
        ]),
        422: z.object({
          message: z.string(),
          reason: z.enum([
            'invalid_template_bundle',
            'signed_appointment',
            'multilingual_support_disabled',
            'no_audio_backed_transcripts',
            'schedule_failed',
          ]),
        }),
      },
      // Discriminated so the contract enforces "inputLanguage iff re-transcribe"
      // rather than trusting the client to pair the two.
      body: z.discriminatedUnion('reTranscribe', [
        z.object({
          reTranscribe: z.literal(false),
          templateBundleId: z.string(),
          outputLanguage: z.string().optional(),
        }),
        z.object({
          reTranscribe: z.literal(true),
          templateBundleId: z.string(),
          inputLanguage: z.string(),
          outputLanguage: z.string().optional(),
        }),
      ]),
      summary:
        'Persist admin-selected appointment settings and regenerate the appointment summary',
    },
    swapPatients: {
      method: 'POST',
      path: '/swap-patients/:tenantId',
      body: z.object({
        appointmentIdA: z.string().uuid(),
        appointmentIdB: z.string().uuid(),
        expectedPatientIdA: z.string().uuid(),
        expectedPatientIdB: z.string().uuid(),
      }),
      responses: {
        200: z.object({
          appointmentIdA: z.string(),
          appointmentIdB: z.string(),
          newPatientIdA: z.string(),
          newPatientIdB: z.string(),
        }),
        422: z.object({
          message: z.string(),
          reason: z.enum([
            'not_found',
            'same_appointment',
            'owner_mismatch',
            'missing_patient',
            'same_patient',
            'deleted',
            'not_completed',
            'synced_to_ehr',
            'external_info_asymmetry',
            'state_changed',
          ]),
        }),
      },
      summary:
        'Swap the patients (and booking identity) between two appointments of the same owner',
    },
  },
  { pathPrefix: '/admin-appointment' },
);
